import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CircleCheck } from 'lucide-react';
import { api, useResource } from './api.js';
import type { FormEvent } from 'react';

// The inbox the form delivers to, also offered directly for anyone who prefers their own email.
const contactEmail='craig@identifrag.com';
export function Contact() {
 const [params] = useSearchParams();
 // Links such as the About page's "Suggest a fragrance" can preselect a topic.
 const [kind, setKind] = useState(() => ['fragrance','alternative','rights'].includes(params.get('kind') ?? '') ? params.get('kind')! : 'business');
 const suggestion = kind==='fragrance'||kind==='alternative';
 const {data,loading}=useResource<{available:boolean}>('/api/v1/contact');
 const [busy,setBusy]=useState(false),[error,setError]=useState(''),[sent,setSent]=useState(false);
 async function submit(event:FormEvent<HTMLFormElement>){
  event.preventDefault();
  if(busy||!data?.available)return;
  const form=event.currentTarget;
  setBusy(true);setError('');setSent(false);
  try{
   await api('/api/v1/contact',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(Object.fromEntries(new FormData(form)))});
   form.reset();setKind('business');setSent(true);
  }catch(e){setError(e instanceof Error?e.message:'Unable to send. Please try again.');}
  finally{setBusy(false);}
 }
 return <article className="about-page contact-page">
  <header className="about-heading">
   <h1>Contact</h1>
   <p className="about-intro">Get in touch. Help the collection grow.</p>
   <p>Have a business enquiry, a fragrance we should include, or a comparison to suggest? We’d like to hear from you.</p>
   <p>Prefer email? Write to <a className="text-link" href={'mailto:'+contactEmail}>{contactEmail}</a>.</p>
   <p>Rights holders can use this address or choose “Rights holder enquiry” below. We aim to respond within five working days.</p>
  </header>
  {sent?<section className="contact-success about-section" id="contact-status" role="status" aria-live="polite">
   <CircleCheck size={28} aria-hidden="true"/>
   <div>
    <h2>Message sent</h2>
    <p>Thank you for getting in touch. We’ll reply by email if your message needs a response.</p>
   </div>
   <button className="text-link" type="button" onClick={()=>setSent(false)}>Send another message</button>
  </section>:<form className="contact-form about-section" onSubmit={submit} aria-describedby="contact-status" aria-busy={busy}>
   <p>We use your details to respond to your enquiry and review suggestions. Read our <Link to="/privacy">Privacy &amp; Cookies notice</Link>.</p>
   <p className="contact-status" id="contact-status" role="status">{loading?'Checking contact availability…':data?.available?'Your message goes to Identifrag. All fields are required unless marked optional.':<>The form is temporarily unavailable. <a className="text-link" href={'mailto:'+contactEmail}>Email Identifrag directly</a>.</>}</p>
   <div hidden aria-hidden="true"><label>Leave this blank<input name="companyFax" tabIndex={-1} autoComplete="off"/></label></div>
   <label htmlFor="contact-kind">What would you like to get in touch about?
    <select id="contact-kind" name="kind" value={kind} onChange={event=>setKind(event.target.value)}>
     <option value="business">Business enquiry</option>
     <option value="fragrance">Suggest a fragrance</option>
     <option value="alternative">Suggest a comparison</option>
     <option value="rights">Rights holder enquiry</option>
    </select>
   </label>
   <div className="contact-fields">
    <label htmlFor="contact-name">Your name<input id="contact-name" name="name" autoComplete="name" maxLength={120} required/></label>
    <label htmlFor="contact-email">Your email<input id="contact-email" name="email" type="email" autoComplete="email" maxLength={254} required/></label>
   </div>
   {(kind==='business'||kind==='rights')&&<label htmlFor="contact-business"><span>Business name <em>(optional)</em></span><input id="contact-business" name="business" autoComplete="organization" maxLength={200}/></label>}
   {suggestion&&<fieldset className="contact-fragrance-fields">
    <legend>{kind==='alternative'?'The fragrance you’re suggesting for comparison':'The fragrance you’re suggesting'}</legend>
    <div className="contact-fields">
     <label htmlFor="contact-brand">Brand<input id="contact-brand" name="brand" maxLength={200} required/></label>
     <label htmlFor="contact-fragrance">Fragrance name<input id="contact-fragrance" name="fragrance" maxLength={200} required/></label>
    </div>
    <label htmlFor="contact-concentration"><span>Concentration <em>(optional)</em></span><input id="contact-concentration" name="concentration" maxLength={100} placeholder="e.g. Eau de Parfum"/></label>
    {kind==='alternative'&&<label htmlFor="contact-original">Fragrance to compare it with<input id="contact-original" name="original" maxLength={300} required aria-describedby="original-help"/><small id="original-help">Include the brand and fragrance name.</small></label>}
   </fieldset>}
   <label htmlFor="contact-website"><span>{suggestion?'Product link':'Website'} <em>(optional)</em></span><input id="contact-website" name="website" type="url" placeholder="https://" maxLength={2000}/></label>
   <label htmlFor="contact-message">{kind==='alternative'?'How do they compare?':'Your message'}
    <textarea id="contact-message" name="message" rows={6} required maxLength={5000} aria-describedby="message-help"/>
    <small id="message-help">{kind==='business'?'Tell us a little about your enquiry and what you have in mind.':kind==='alternative'?'Tell us what smells similar and what makes the fragrances different.':'Tell us why you’d like to see this fragrance in the collection.'}</small>
   </label>
   <div className="contact-submit">
    {error&&<p className="error-message" role="alert">{error}</p>}
    <button className="button" type="submit" disabled={busy||!data?.available} aria-describedby="contact-status">{busy?'Sending…':'Send message'}</button>
   </div>
  </form>}
 </article>;
}
